/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */

import { Levels } from "@nuance/hierarchical-settings-ui-domain";
import { IFieldDefinitionPanelSpinData, IFieldDefinitionPanelSpinState } from ".";
import { getLogger, ILogger } from "@nuance/hux-diagnostics";
import { BaseCommands, FormErrors, IFieldChangeData } from "../../State";
import { CmdBuilder } from "@nuance/hap-components";

const logger: ILogger = getLogger();

export class FieldDefinitionPanelSpinCommands extends BaseCommands<
    IFieldDefinitionPanelSpinData,
    IFieldDefinitionPanelSpinState
> {
    constructor() {
        super();
        const cb = new CmdBuilder();
        const cmds = {
            onBlur: cb.bindCmd<IFieldDefinitionPanelSpinState, string>(this.OnBlurHandler),
            onTextChanged: cb.bindCmd<IFieldDefinitionPanelSpinState, IFieldChangeData>(
                this.OnTextChangedHandler
            ),
            onAccessLevelChanged: cb.bindCmd<IFieldDefinitionPanelSpinState, Levels>(
                this.OnAccessLevelChangedHandler
            ),
            onAuditChanged: cb.bindCmd(this.OnAuditChangedHandler),
            onHelpTextChange: cb.bindCmd(this.OnHelpTextChangeHandler),

            onDefaultValueChanged: cb.bindCmd(this.onDefaultValueChangedHandler),
            onMinValueChanged: cb.bindCmd(this.onMinValueChangedHandler),
            onMaxValueChanged: cb.bindCmd(this.onMaxValueChangedHandler),
            onStepChanged: cb.bindCmd(this.onStepChangedHandler),
            onSuffixChanged: cb.bindCmd(this.onSuffixChangeHandler)
        };

        this.Cmds = cmds;
        this.Reducer = cb.buildReducer<IFieldDefinitionPanelSpinState>(cmds);
    }

    readonly Cmds;
    readonly Reducer;

    Save = (state: IFieldDefinitionPanelSpinState): IFieldDefinitionPanelSpinData => {
        logger.logInformation("Saving spinner properties");

        // important to make a copy!
        return { ...state.data };
    };

    /**
     * Validates the form fields.
     *
     * @param state Current state (immutable)
     * @returns Form errors
     */
    Validate(state: IFieldDefinitionPanelSpinState): FormErrors<IFieldDefinitionPanelSpinData> {
        const errors: FormErrors<IFieldDefinitionPanelSpinData> = {};

        // settingName validation
        if (state.data.settingName === "") {
            errors.settingName = "Setting name is required.";
        } else {
            if (state.common.validateSettingName === undefined) {
                logger.logError("Setting name validation function not hooked up");
                throw new Error("Unable to validate setting name");
            }

            errors.settingName = state.common.validateSettingName(
                state.data.settingName,
                state.common.originalSettingName
            );
        }

        // label
        if (state.data.title === "") {
            errors.title = "Label is required.";
        }

        // help text
        if (!state.data.helpTextValid) {
            errors.helpTextValid = "Help text is not valid.";
        }

        //default value
        if (state.data.defaultValue < state.data.minValue) {
            errors.defaultValue = "Default value is less than min value.";
        } else if (state.data.defaultValue > state.data.maxValue) {
            errors.defaultValue = "Default value is greater than max value.";
        }
        //min, max value
        if (state.data.minValue > state.data.maxValue) {
            errors.minValue = "The Min value must be less than or equal to the Max value.";
        }
        if (state.data.maxValue < state.data.minValue) {
            errors.maxValue = "The Max value must be greater than or equal to the Min value.";
        }

        return errors;
    }

    /////////////////////////////////////////////////////////////////
    // Command handlers
    /////////////////////////////////////////////////////////////////

    onDefaultValueChangedHandler = (state: IFieldDefinitionPanelSpinState, newValue?: string) => {
        if (newValue !== undefined && !isNaN(+newValue)) {
            state.data.defaultValue = +newValue;
            state.isDirty = true;
        }

        state.errors = this.Validate(state);
        state.isValid = this.IsValid(state);
        return state;
    };

    onMinValueChangedHandler = (state: IFieldDefinitionPanelSpinState, newValue?: string) => {
        if (newValue !== undefined && !isNaN(+newValue)) {
            const min: number = +newValue;
            state.data.minValue = min;
            state.isDirty = true;
        }

        state.errors = this.Validate(state);
        state.isValid = this.IsValid(state);
        return state;
    };

    onMaxValueChangedHandler = (state: IFieldDefinitionPanelSpinState, newValue?: string) => {
        if (newValue !== undefined && !isNaN(+newValue)) {
            const max: number = +newValue;
            state.data.maxValue = max;
            state.isDirty = true;
        }

        state.errors = this.Validate(state);
        state.isValid = this.IsValid(state);
        return state;
    };

    onStepChangedHandler = (state: IFieldDefinitionPanelSpinState, newValue?: string) => {
        if (newValue !== undefined && !isNaN(+newValue)) {
            const step = +newValue;
            state.data.step = step;
            state.isDirty = true;
        }

        return state;
    };

    onSuffixChangeHandler = (state: IFieldDefinitionPanelSpinState, newValue?: string) => {
        if (newValue !== undefined) {
            state.data.suffix = newValue;
            state.isDirty = true;
        }

        return state;
    };
}
